import { createAsyncThunk } from '@reduxjs/toolkit';

import instance from 'configs/requests';
import store from 'store/storeConfig';
import { formatPatient } from 'utils/Helpers/PatientStoreFunctions';

export const getPatient = createAsyncThunk(
  'patient/getPatient',
  async ({
    uid,
    accessToken,
    usePatientApi
  }: {
    uid: string;
    accessToken?: string;
    usePatientApi?: boolean;
  }) => {
    const { data } = await instance.get(
      `/${usePatientApi ? 'patient' : 'clinician'}/Patient/${uid}`,
      {
        headers: {
          Authorization: 'Bearer ' + accessToken
        }
      }
    );

    return formatPatient(data) as IPatient;
  }
);

export const getPatientDevices = createAsyncThunk('patient/getDevices', async (uid: string) => {
  const { data } = await instance.get(`/clinician-gateway/patient/${uid}/device`);
  return data as IDeviceListItem[];
});

export const fetchAllMeasurementPages = async (params: Record<string, unknown>) => {
  const res = await instance.get('/clinician/Measurement', {
    params
  });
  let result = res?.data;
  let lastRecordsSent = result.recordsSent;
  let lastHWM = result.hwm;
  while (lastRecordsSent === 1000) {
    const res = await instance.get('/clinician/Measurement', {
      params: { ...params, highWaterMark: lastHWM }
    });
    lastRecordsSent = res.data.recordsSent;
    lastHWM = res.data.hwm;
    const newMeasurements = result.measurements;
    Object.entries(res.data.measurements).map(([key, value]: any) => {
      if (newMeasurements[key]) {
        newMeasurements[key].measurements = [
          ...newMeasurements[key].measurements,
          ...value.measurements
        ];
      } else {
        newMeasurements[key] = value;
      }
    });
    result = {
      recordsSent: result.recordsSent + res.data.recordsSent,
      measurements: newMeasurements
    };
  }
  return result;
};

export const getMeasurement = createAsyncThunk('patient/getMeasurement', async (uid: string) => {
  const { patient, settings } = store.getState();
  const startDate = patient.vitals.measurementGraphRange.startDate.toUTC().toString();
  const endDate = patient.vitals.measurementGraphRange.endDate.toUTC().toString();

  return fetchAllMeasurementPages({
    specificUid: uid,
    class: JSON.stringify(
      settings.citc?.measurementClasses?.map((x: any) => {
        return { class: x.className };
      })
    ),
    startDate,
    endDate
  });
});

export const getEgvs = createAsyncThunk(
  'patient/getEgvs',
  async ({
    uid,
    unit,
    accessToken
  }: {
    uid: string;
    unit?: IGlucoseUnit;
    accessToken?: string;
  }) => {
    const { patient, settings } = store.getState();
    const startDate = patient.vitals.measurementGraphRange.startDate;
    const endDate = patient.vitals.measurementGraphRange.endDate;

    const params = {
      uid,
      from: startDate.toString(),
      to: endDate.toString(),
      unit: unit || settings.unitPreferences.glucose,
      report: true
    };

    const { data } = await instance.get('/clinician-gateway/dexcom/egvs', {
      params,
      headers: {
        Authorization: 'Bearer ' + accessToken
      }
    });
    return data;
  }
);

export const getRefRange = createAsyncThunk(
  'patient/getRefRange',
  async ({ uid, accessToken }: { uid: string; accessToken?: string }) => {
    const { data } = await instance.get(`/clinician/Patient/${uid}/RefRange`, {
      headers: {
        Authorization: 'Bearer ' + accessToken
      }
    });
    return data;
  }
);

export const getBaselines = createAsyncThunk('patient/getBaselines', async (uid: string) => {
  const { data } = await instance.get<IBaselineResponse>(
    `/clinician-gateway/patient/${uid}/baselines`
  );
  return data.baselines || [];
});
